export const isPhoneContactPickerSupported = () => (
  typeof navigator !== 'undefined'
  && Boolean(navigator.contacts)
  && typeof navigator.contacts.select === 'function'
);

const firstTextValue = (value) => {
  const values = Array.isArray(value) ? value : [value];
  return values.find((item) => typeof item === 'string' && item.trim())?.trim() || '';
};

const preferredPhoneValue = (value) => {
  const values = (Array.isArray(value) ? value : [value])
    .filter((item) => typeof item === 'string' && item.trim())
    .map((item) => item.trim());
  return values.find((item) => {
    const digitCount = item.replace(/\D/g, '').length;
    return digitCount >= 7 && digitCount <= 15;
  }) || values[0] || '';
};

export const preparePhoneContactSelection = (contacts = []) => {
  const preparedContacts = [];
  const skipped = [];

  contacts.forEach((contact) => {
    const name = firstTextValue(contact?.name);
    const phone = preferredPhoneValue(contact?.tel);
    if (name && phone) {
      preparedContacts.push({ name, phone });
      return;
    }
    skipped.push({
      name,
      phone,
      reason: !name && !phone
        ? 'Contact did not share a name or phone number'
        : !name ? 'Contact did not share a name' : 'Contact did not share a phone number',
    });
  });

  return {
    contacts: preparedContacts,
    selectedCount: contacts.length,
    ...(skipped.length > 0 ? { skipped } : {}),
  };
};

export const preparePhoneContacts = (contacts = []) => preparePhoneContactSelection(contacts).contacts;

export const getPhoneContactKey = (contact = {}) => {
  const normalizedPhone = String(contact.phone || '').replace(/[^\d+]/g, '').toLowerCase();
  const normalizedName = String(contact.name || '').trim().replace(/\s+/g, ' ').toLocaleLowerCase();
  return `${normalizedPhone}::${normalizedName}`;
};

export const mergePhoneContacts = (currentContacts = [], incomingContacts = []) => {
  const contactsByKey = new Map();
  [...currentContacts, ...incomingContacts].forEach((contact) => {
    const prepared = preparePhoneContacts([{ name: contact?.name, tel: contact?.phone }])[0];
    if (!prepared) return;
    const key = getPhoneContactKey(prepared);
    if (!contactsByKey.has(key)) contactsByKey.set(key, prepared);
  });
  return Array.from(contactsByKey.values());
};

const decodeQuotedPrintable = (value) => {
  const escapedPercent = String(value || '').replace(/%/g, '%25');
  const encoded = escapedPercent.replace(/=([0-9a-f]{2})/gi, '%$1');
  try {
    return decodeURIComponent(encoded);
  } catch {
    return value;
  }
};

const decodeVCardValue = (value, propertyDefinition = '') => {
  const decoded = /encoding=quoted-printable/i.test(propertyDefinition)
    ? decodeQuotedPrintable(value)
    : String(value || '');
  return decoded
    .replace(/\\n/gi, ' ')
    .replace(/\\([,;\\])/g, '$1')
    .trim();
};

const unfoldVCard = (source) => String(source || '')
  .replace(/^\uFEFF/, '')
  .replace(/\r\n|\r/g, '\n')
  .replace(/=\n/g, '')
  .replace(/\n[ \t]/g, '');

export const parseVCardContacts = (source) => {
  const cards = unfoldVCard(source).match(/BEGIN:VCARD[\s\S]*?END:VCARD/gi) || [];
  const contacts = cards.map((card) => {
    let formattedName = '';
    let structuredName = '';
    const phoneNumbers = [];

    card.split(/\r?\n/).forEach((line) => {
      const separatorIndex = line.indexOf(':');
      if (separatorIndex < 0) return;
      const definition = line.slice(0, separatorIndex);
      const value = line.slice(separatorIndex + 1);
      const propertyName = definition
        .split(';')[0]
        .split('.')
        .pop()
        .toUpperCase();

      if (propertyName === 'FN' && !formattedName) {
        formattedName = decodeVCardValue(value, definition);
      } else if (propertyName === 'N' && !structuredName) {
        const parts = value.split(';').map((part) => decodeVCardValue(part, definition));
        structuredName = [parts[1], parts[2], parts[0]].filter(Boolean).join(' ').trim();
      } else if (propertyName === 'TEL') {
        const phone = decodeVCardValue(value, definition).replace(/^tel:/i, '').trim();
        if (phone && !phoneNumbers.includes(phone)) phoneNumbers.push(phone);
      }
    });

    return {
      name: [formattedName || structuredName],
      tel: phoneNumbers,
    };
  });

  return preparePhoneContactSelection(contacts);
};

export const readVCardContacts = async (file) => {
  if (!file || (typeof file.arrayBuffer !== 'function' && typeof file.text !== 'function')) {
    throw new Error('VCARD_FILE_UNREADABLE');
  }
  if (file.size > 10 * 1024 * 1024) {
    throw new Error('VCARD_FILE_TOO_LARGE');
  }

  let source = '';
  try {
    if (typeof file.arrayBuffer === 'function') {
      const buffer = await file.arrayBuffer();
      const bytes = new Uint8Array(buffer);
      let encoding = 'utf-8';
      let offset = 0;

      if (bytes[0] === 0xff && bytes[1] === 0xfe) {
        encoding = 'utf-16le';
        offset = 2;
      } else if (bytes[0] === 0xfe && bytes[1] === 0xff) {
        encoding = 'utf-16be';
        offset = 2;
      } else {
        const sample = bytes.slice(0, Math.min(bytes.length, 200));
        const evenNulls = sample.filter((value, index) => index % 2 === 0 && value === 0).length;
        const oddNulls = sample.filter((value, index) => index % 2 === 1 && value === 0).length;
        if (oddNulls > sample.length / 8) encoding = 'utf-16le';
        if (evenNulls > sample.length / 8) encoding = 'utf-16be';
      }
      if (typeof TextDecoder !== 'undefined') {
        source = new TextDecoder(encoding).decode(bytes.slice(offset));
      } else if (encoding === 'utf-16le' || encoding === 'utf-16be') {
        const littleEndian = encoding === 'utf-16le';
        const characters = [];
        for (let index = offset; index + 1 < bytes.length; index += 2) {
          characters.push(String.fromCharCode(littleEndian
            ? bytes[index] | (bytes[index + 1] << 8)
            : (bytes[index] << 8) | bytes[index + 1]));
        }
        source = characters.join('');
      } else {
        source = await file.text();
      }
    } else {
      source = await file.text();
    }
  } catch {
    try {
      source = await file.text();
    } catch {
      throw new Error('VCARD_FILE_UNREADABLE');
    }
  }

  const result = parseVCardContacts(source);
  if (result.selectedCount === 0) {
    throw new Error('VCARD_FILE_INVALID');
  }
  return result;
};

export const pickPhoneContacts = async () => {
  if (!isPhoneContactPickerSupported()) {
    throw new Error('CONTACT_PICKER_UNSUPPORTED');
  }

  const availableProperties = typeof navigator.contacts.getProperties === 'function'
    ? await navigator.contacts.getProperties()
    : ['name', 'tel'];
  const properties = ['name', 'tel'].filter((property) => availableProperties.includes(property));

  if (!properties.includes('tel') || !properties.includes('name')) {
    throw new Error('CONTACT_FIELDS_UNAVAILABLE');
  }

  const contacts = await navigator.contacts.select(properties, { multiple: true });
  return preparePhoneContactSelection(contacts);
};
